import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import CouponBanner from './CouponBanner';
import {
  categoryUrl,
  resolveCategoryMeta,
} from './categoryDirectoryConfig';
import './VisitResturants.css';
import './CategoryDirectory.css';
import './DirectorySearch.css';

const configuredApiBase =
  typeof process !== 'undefined' && process.env
    ? process.env.REACT_APP_GOOURHUB_BUSINESS_API
    : '';

const API_BASE_URL = (
  configuredApiBase || 'https://business-api.goourhub.com'
).replace(/\/+$/, '');

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
};

const categoryNameFromItem = (item) => {
  if (typeof item === 'string') {
    return item;
  }

  if (!item || typeof item !== 'object') {
    return '';
  }

  return firstText(
    item.value,
    item.category,
    item.slug,
    item.name,
    item.label,
    item.key
  );
};

const categoryCountFromItem = (item) => {
  if (!item || typeof item !== 'object') {
    return 0;
  }

  const count = Number(
    item.count ??
      item.business_count ??
      item.businessCount ??
      item.total ??
      item.totalBusinesses ??
      0
  );

  return Number.isFinite(count) && count >= 0 ? count : 0;
};

function AllBusinessCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [reloadNumber, setReloadNumber] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    const loadCategories = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const response = await fetch(
          `${API_BASE_URL}/api/directory/categories`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          }
        );

        let payload;

        try {
          payload = await response.json();
        } catch {
          throw new Error('The category directory returned an invalid response.');
        }

        if (!response.ok || payload?.success === false) {
          throw new Error(
            payload?.message || `Unable to load categories (${response.status}).`
          );
        }

        const rawCategories = Array.isArray(payload?.categories)
          ? payload.categories
          : payload?.categories && typeof payload.categories === 'object'
            ? Object.entries(payload.categories).map(([value, count]) => ({
                value,
                count,
              }))
            : [];

        const uniqueCategories = new Map();

        rawCategories.forEach((item) => {
          const rawName = categoryNameFromItem(item);

          if (!rawName) {
            return;
          }

          const meta = resolveCategoryMeta(rawName);
          const count = categoryCountFromItem(item);
          const existing = uniqueCategories.get(meta.slug);

          uniqueCategories.set(meta.slug, {
            ...meta,
            count: Math.max(existing?.count || 0, count),
          });
        });

        const normalizedCategories = Array.from(uniqueCategories.values()).sort(
          (left, right) =>
            left.label.localeCompare(right.label, undefined, {
              sensitivity: 'base',
            })
        );

        if (normalizedCategories.length === 0) {
          throw new Error('No approved business categories were returned.');
        }

        if (!controller.signal.aborted) {
          setCategories(normalizedCategories);
        }
      } catch (error) {
        if (error.name !== 'AbortError' && !controller.signal.aborted) {
          console.error('Unable to load business categories.', error);
          setErrorMessage(
            error.message || 'Unable to load categories. Please try again.'
          );
          setCategories([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadCategories();

    return () => controller.abort();
  }, [reloadNumber]);


  const normalizedSearch = searchQuery.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!normalizedSearch) {
      return categories;
    }

    return categories.filter((category) =>
      [category.label, category.value, category.slug]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [categories, normalizedSearch]);

  const clearSearch = () => setSearchQuery('');

  return (
    <div className="categories-container category-directory-page">
      <CouponBanner />

      <div className="category-section">
        <h2 className="category-header">All Business Categories</h2>

        {!loading && !errorMessage && categories.length > 0 && (
          <section className="directory-search-panel" aria-label="Category search">
            <label className="directory-search__label" htmlFor="all-category-search">
              Search categories
            </label>

            <div className="directory-search__control">
              <input
                id="all-category-search"
                type="search"
                className="directory-search__input"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Try restaurants, groceries, plumbing, photography..."
                autoComplete="off"
              />

              {searchQuery && (
                <button
                  type="button"
                  className="directory-search__clear"
                  onClick={clearSearch}
                >
                  Clear
                </button>
              )}
            </div>

            <p className="directory-search__status" aria-live="polite">
              {searchQuery
                ? `${filteredCategories.length} of ${categories.length} categories matched.`
                : `${categories.length} approved business categories.`}
            </p>
          </section>
        )}

        {loading && <p role="status">Loading categories...</p>}

        {!loading && errorMessage && (
          <div>
            <p role="alert">{errorMessage}</p>
            <button
              type="button"
              onClick={() => setReloadNumber((value) => value + 1)}
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !errorMessage && (
          <div className="new-tile-container-new">
            {filteredCategories.map((category) => (
              <Link
                key={category.slug}
                to={categoryUrl(category.value)}
                className="new-tile tile-clickable category-directory-tile"
              >
                <span className="tile-glare" aria-hidden="true" />
                <span className="icon-for-new-tile">{category.icon}</span>
                <span className="tile-name">{category.label}</span>
                <span className="category-directory-count">
                  {category.count} {category.count === 1 ? 'business' : 'businesses'}
                </span>
              </Link>
            ))}
          </div>
        )}

        {!loading &&
          !errorMessage &&
          categories.length > 0 &&
          filteredCategories.length === 0 && (
            <div className="directory-search-empty">
              No categories matched <strong>{searchQuery}</strong>.
              <button type="button" onClick={clearSearch}>
                Show all categories
              </button>
            </div>
          )}
      </div>

      <div className="back-home-tile">
        <Link to="/" className="back-home-link">
          Go Back to Homepage
        </Link>
      </div>
    </div>
  );
}

export default AllBusinessCategories;
